import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getService, getProblem, getArticle, INDUSTRIES } from "../data/site";
import { getCaseStudy } from "../data/caseStudies";

const BASE_URL = "https://silvertreesolutions.co";
const SITE_NAME = "Silvertree Solutions";
const DEFAULT_DESC =
  "Oklahoma-based business IT support and technology advisory. Responsive help desk, managed IT, and security — fixed monthly plans from $495/month including up to 5 users — plus assessments and consulting for growing businesses.";

const STATIC_META = {
  "/": {
    title: "Silvertree Solutions | Business IT Support & Technology Advisory in Oklahoma",
    description: DEFAULT_DESC,
  },
  "/business-it-support": {
    title: "Business IT Support in Oklahoma | Silvertree Solutions",
    description:
      "Your IT department—without hiring an IT department. Responsive Oklahoma IT support with visible pricing: Assist $495/mo, Managed $745/mo, Secure $995/mo — each includes up to 5 users. 30-minute response promise.",
    schema: "ITSupport",
  },
  "/it-support/assist": {
    title: "Silvertree Assist — Reliable IT Help When Something Breaks | Silvertree Solutions",
    description:
      "Reliable IT support when your team needs it. Business-hours help desk and Microsoft 365 support — $495/month including up to 5 users, +$99/month per additional user.",
  },
  "/it-support/managed": {
    title: "Silvertree Managed — Stop Managing IT Yourself | Silvertree Solutions",
    description:
      "Your outsourced IT department: support, devices, patching, Microsoft 365, monitoring, and administration. $745/month including up to 5 users, +$149/month per additional user. Most popular plan.",
  },
  "/it-support/secure": {
    title: "Silvertree Secure — Managed IT with 24/7 Security Monitoring | Silvertree Solutions",
    description:
      "Managed IT plus 24/7 managed security monitoring, identity protection, and enhanced endpoint security. $995/month including up to 5 users, +$199/month per additional user.",
  },
  "/switch-it-provider": {
    title: "Switch IT Providers Without the Drama | Silvertree Solutions",
    description:
      "Unhappy with your current MSP or IT person? Silvertree plans and manages the whole transition — documentation, handoff, overlap, and day-one support. Plan your IT transition.",
  },
  "/services": {
    title: "IT Consulting Services | Silvertree Solutions",
    description:
      "12 practical IT consulting services: IT strategy & roadmapping, cybersecurity assessment, systems integration, software modernization, fractional CTO, and more.",
  },
  "/problems": {
    title: "Problems We Solve | Silvertree Solutions",
    description:
      "From systems that don't communicate to outdated software and cybersecurity worries — the 10 technology problems we help growing businesses solve.",
  },
  "/methodology": {
    title: "Our Methodology | Silvertree Solutions",
    description:
      "A proven six-phase methodology that takes your organization from discovery to lasting results — listen first, design deliberately, execute with discipline.",
  },
  "/industries": {
    title: "Industries We Serve | Silvertree Solutions",
    description:
      "IT consulting for healthcare, professional services, retail & distribution, manufacturing, construction, financial services, nonprofits, and regulated industries.",
  },
  "/resources": {
    title: "Free IT Resources: Checklists, Templates & Guides | Silvertree Solutions",
    description:
      "Free downloadable IT checklists, templates, and scorecards — cybersecurity readiness, technology roadmaps, AI readiness, vendor selection, and more.",
  },
  "/insights": {
    title: "Insights & Articles | Silvertree Solutions",
    description:
      "Practical, jargon-free articles on technology strategy, modernization, systems integration, and executive technology leadership for growing businesses.",
  },
  "/case-studies": {
    title: "Experience & Results | Silvertree Solutions",
    description:
      "The outcomes our IT consulting work delivers — connected systems, reduced risk, clear roadmaps — built on 20+ years of technology leadership. Honest proof, never invented results.",
  },
  "/pricing": {
    title: "Pricing & Engagement Models | Silvertree Solutions",
    description:
      "Transparent pricing: Technology Health Assessments from $495 / $995 / $1,995+, project-based consulting, fractional CTO retainers, and focused assessments. How our pricing works.",
  },
  "/about": {
    title: "About Us | Silvertree Solutions",
    description:
      "A U.S. IT consulting and technology advisory firm headquartered in Oklahoma, serving growing businesses nationwide with clarity, honesty, and practical guidance.",
  },
  "/contact": {
    title: "Let's Solve What's Holding Your Business Back | Silvertree Solutions",
    description:
      "Tell us what isn't working — outdated systems, security concerns, rising costs, or manual processes — and we'll recommend a practical next step. Technology Health Assessments start at $495.",
  },
  "/privacy": { title: "Privacy Policy | Silvertree Solutions", description: "How Silvertree Solutions collects, uses, and protects the information you share through our website." },
  "/terms": { title: "Terms of Use | Silvertree Solutions", description: "Terms governing your use of the Silvertree Solutions website and its resources." },
  "/assessments/it-assessment": {
    title: "General Technology Health Assessment | Silvertree Solutions",
    description:
      "A structured review of your technology strategy, spend, systems, and risk — delivered as a prioritized, budget-aware action plan for leadership.",
  },
  "/assessments/ai-readiness": {
    title: "AI Readiness Assessment | Silvertree Solutions",
    description:
      "A grounded, hype-free evaluation of whether your data, processes, and governance are ready for practical AI adoption — with a shortlist of first use cases.",
  },
  "/assessments/cybersecurity": {
    title: "Cybersecurity Assessment | Silvertree Solutions",
    description:
      "A calm, business-language review of your real cyber exposure and the protections that matter most for your size — no fear, no jargon.",
  },
  "/webinars": {
    title: "Free Webinar: Reducing Technology Risk Before a Major Project | Silvertree Solutions",
    description:
      "Register free for our on-demand session on de-risking major technology initiatives — scope clarity, real ownership, and vendor accountability.",
  },
  "/technology-health-snapshot": {
    title: "Technology Health Assessment | Silvertree Solutions",
    description:
      "Find hidden technology risks, wasted costs, and practical next steps with Silvertree's Technology Health Assessment, starting at $495.",
    schema: "Snapshot",
  },
  "/technology-health-snapshot/thank-you": {
    title: "Thank You — Technology Health Assessment | Silvertree Solutions",
    description:
      "Your Technology Health Assessment request has been received. Silvertree will confirm fit, scope, timing, and next steps.",
  },
  "/admin": { title: "Admin | Silvertree Solutions", description: "Silvertree Solutions admin dashboard." },
  "/admin/login": { title: "Admin Sign In | Silvertree Solutions", description: "Sign in to the Silvertree Solutions admin dashboard." },
  "/portal": { title: "Client Portal | Silvertree Solutions", description: "View your engagements and deliverables." },
  "/portal/login": { title: "Client Portal Sign In | Silvertree Solutions", description: "Sign in to the Silvertree Solutions client portal." },
};

const resolveMeta = (pathname) => {
  if (STATIC_META[pathname]) return STATIC_META[pathname];
  let m;
  if ((m = pathname.match(/^\/services\/([^/]+)$/))) {
    const s = getService(m[1]);
    if (s) return { title: `${s.name} | ${SITE_NAME}`, description: s.summary, schema: "Service", name: s.name };
  }
  if ((m = pathname.match(/^\/problems\/([^/]+)$/))) {
    const p = getProblem(m[1]);
    if (p) return { title: `${p.title} — Problems We Solve | ${SITE_NAME}`, description: p.angle };
  }
  if ((m = pathname.match(/^\/industries\/([^/]+)$/))) {
    const ind = INDUSTRIES.find((i) => i.slug === m[1]);
    if (ind)
      return {
        title: `IT Consulting for ${ind.name} | ${SITE_NAME}`,
        description: ind.summary,
      };
  }
  if ((m = pathname.match(/^\/case-studies\/([^/]+)$/))) {
    const cs = getCaseStudy(m[1]);
    if (cs) return { title: `${cs.title} — Engagement Walkthrough | ${SITE_NAME}`, description: cs.summary };
  }
  if ((m = pathname.match(/^\/insights\/([^/]+)$/))) {
    const a = getArticle(m[1]);
    if (a)
      return {
        title: `${a.title} | Silvertree Insights`,
        description: a.excerpt,
        type: "article",
        schema: "Article",
        headline: a.title,
      };
  }
  return { title: STATIC_META["/"].title, description: DEFAULT_DESC };
};

const setMetaTag = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setJsonLd = (data) => {
  let el = document.getElementById("page-jsonld");
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "page-jsonld";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const RouteSeo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = resolveMeta(pathname);
    const url = `${BASE_URL}${pathname === "/" ? "/" : pathname}`;

    document.title = meta.title;
    setMetaTag("name", "description", meta.description);
    setMetaTag("name", "robots", pathname.startsWith("/admin") || pathname.startsWith("/portal") ? "noindex, nofollow" : "index, follow");
    setMetaTag("property", "og:title", meta.title);
    setMetaTag("property", "og:description", meta.description);
    setMetaTag("property", "og:url", url);
    setMetaTag("property", "og:type", meta.type || "website");

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    if (meta.schema === "Article") {
      setJsonLd({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: meta.headline,
        description: meta.description,
        url,
        author: { "@type": "Organization", name: SITE_NAME },
        publisher: { "@type": "Organization", name: SITE_NAME, url: BASE_URL },
      });
    } else if (meta.schema === "Snapshot") {
      setJsonLd({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: "Technology Health Assessment",
            description: meta.description,
            url,
            provider: { "@type": "Organization", name: SITE_NAME, url: BASE_URL },
            areaServed: { "@type": "State", name: "Oklahoma" },
            offers: [
              { "@type": "Offer", name: "Launch / Basic", price: "495", priceCurrency: "USD", url },
              { "@type": "Offer", name: "Expanded", price: "995", priceCurrency: "USD", url },
              {
                "@type": "Offer",
                name: "Comprehensive",
                priceCurrency: "USD",
                priceSpecification: { "@type": "PriceSpecification", minPrice: "1995", priceCurrency: "USD" },
                url,
              },
            ],
          },
          {
            "@type": "FAQPage",
            mainEntity: [
              ["What is a Technology Health Assessment?", "It is a practical review of the technology your business relies on. You receive a health score, key risks and opportunities, and a prioritized action plan."],
              ["Is this a full IT or cybersecurity audit?", "No. The Launch / Basic level is a high-level snapshot. It does not include penetration testing, exhaustive configuration testing, formal compliance certification, or forensic work unless a separate scope says otherwise."],
              ["What is included in the $495 level?", "The Launch / Basic level reviews the core areas at a high level and includes your Technology Health Score, Top 5 risks, Top 5 opportunities, immediate actions, a 30-day, 90-day, and 12-month plan, estimated business impact, and a findings walkthrough."],
              ["Do I have to hire Silvertree to make the improvements?", "No. You may use the recommendations with Silvertree, your current technology provider, or your own team."],
              ["How do I start?", "Select \u201cStart My $495 Assessment,\u201d complete the short form, and choose a time to talk if scheduling is available."],
            ].map(([q, a]) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          },
        ],
      });
    } else if (meta.schema === "ITSupport") {
      setJsonLd({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            serviceType: "Managed IT Support",
            name: "Business IT Support",
            description: meta.description,
            url,
            provider: { "@type": "Organization", name: SITE_NAME, url: BASE_URL, telephone: "(405) 254-6048" },
            areaServed: { "@type": "State", name: "Oklahoma" },
            offers: [
              { name: "Silvertree Assist", price: "495" },
              { name: "Silvertree Managed", price: "745" },
              { name: "Silvertree Secure", price: "995" },
            ].map((o) => ({
              "@type": "Offer",
              name: o.name,
              url,
              priceCurrency: "USD",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: o.price,
                priceCurrency: "USD",
                unitText: "per month (includes up to 5 users)",
              },
            })),
          },
          {
            "@type": "FAQPage",
            mainEntity: [
              ["How does plan pricing work?", "Each plan is a fixed monthly package including support for up to 5 users: Assist $495/month, Managed $745/month, Secure $995/month. Only users beyond five add a per-user charge of $99, $149, or $199 per additional user respectively."],
              ["Is onsite support included?", "Plans are remote-first. Onsite support is available when needed and may be billed separately unless your agreement states otherwise."],
              ["What does the 30-minute response promise mean?", "Qualifying urgent business-hours requests (M–F, 8am–5pm CT) receive a human response within 30 minutes. Response means a real person engages with your request; resolution time depends on the issue."],
              ["Are Microsoft 365 licenses included?", "Microsoft 365 licensing is available through Silvertree and is billed separately unless specifically included in your agreement."],
              ["Can I buy just a one-time assessment?", "Yes. The Technology Health Snapshot starts at $495 and can be purchased without recurring support. It may be applied toward qualifying Managed or Secure onboarding within 30 days."],
            ].map(([q, a]) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          },
        ],
      });
    } else if (meta.schema === "Service") {
      setJsonLd({
        "@context": "https://schema.org",
        "@type": "Service",
        name: meta.name,
        description: meta.description,
        url,
        provider: { "@type": "Organization", name: SITE_NAME, url: BASE_URL },
        areaServed: "United States",
      });
    } else {
      setJsonLd(null);
    }

    if (window.gtag) {
      window.gtag("event", "page_view", { page_path: pathname, page_title: meta.title });
    }
  }, [pathname]);

  return null;
};
